// Lịch làm việc HANA (thay WorkCalendar của LOGI). Mọi giờ tính theo giờ Việt Nam (UTC+7, không đổi giờ mùa).
// Màn và worker KHÔNG tự viết 08:00 / 17:30 / getDay() / +N giờ — gọi qua đây.
import { parseHHMM, type Holiday, type Minute, type WorkConfig } from './config';

const MIN = 60_000;
const DAY_MIN = 1440;
const VN_OFFSET_MIN = 7 * 60;

/** Mốc thời gian dạng "phút địa phương VN kể từ 1970-01-01 00:00 VN". */
type Local = number;

function toLocal(t: string | Date): Local {
  const ms = typeof t === 'string' ? Date.parse(t) : t.getTime();
  if (Number.isNaN(ms)) throw new Error(`Thời điểm không hợp lệ: ${String(t)}`);
  return Math.floor(ms / MIN) + VN_OFFSET_MIN;
}

const pad = (n: number) => String(n).padStart(2, '0');

function dayKey(dayNo: number): string {
  const d = new Date(dayNo * DAY_MIN * MIN);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

function dayNoOf(key: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) throw new Error(`Ngày không hợp lệ: ${key}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / (DAY_MIN * MIN);
}

/** ISO giờ VN, vd 2026-10-24T09:30:00+07:00 */
function toIso(l: Local): string {
  const day = Math.floor(l / DAY_MIN);
  const m = l - day * DAY_MIN;
  return `${dayKey(day)}T${pad(Math.floor(m / 60))}:${pad(m % 60)}:00+07:00`;
}

/** 0 = CN … 6 = T7 (1970-01-01 là thứ Năm). */
const weekdayOf = (dayNo: number) => (((dayNo + 4) % 7) + 7) % 7;

export interface WorkCalendar {
  readonly config: WorkConfig;
  /** ngày (YYYY-MM-DD hoặc thời điểm) có phải ngày làm việc */
  isWorkDay(day: string | Date): boolean;
  /** lễ / nghỉ công ty của ngày, nếu có */
  holidayOf(day: string): Holiday | undefined;
  /** các ca của ngày, phút trong ngày */
  shiftsOf(day: string): Array<readonly [Minute, Minute]>;
  /** số giờ làm việc chuẩn của ngày theo ca (LOGI `dayHours`) */
  dayHours(day: string): number;
  /** số giờ có mặt từ `a` (check-in) tới `b` (check-out), trừ nghỉ trưa, vẫn đếm ngoài ca (OT) (LOGI `presenceHours(a,b)`).
   *  Dạng một tham số `presenceHours(day)` là tên cũ của `dayHours` — chỉ giữ để vùng Mee chưa đổi kịp, đừng dùng mới. */
  presenceHours(a: string | Date, b: string | Date): number;
  /** @deprecated dùng `dayHours(day)` */
  presenceHours(day: string): number;
  isHoliday(day: string): boolean;
  /** loại ngày: lễ / nghỉ công ty / làm bù / thứ Bảy nửa ngày / ngày làm việc / nghỉ cuối tuần */
  dayKind(day: string): 'holiday' | 'companyHoliday' | 'makeup' | 'halfDay' | 'work' | 'off';
  /** các kỳ nghỉ lễ giao với [from, to] */
  holidaysInRange(from: string, to: string): Holiday[];
  prevWorkDay(day: string): string;
  nextWorkDay(day: string): string;
  /** số ngày công chuẩn của tháng "YYYY-MM": mỗi ngày = giờ ngày đó / giờ ngày đủ (T7 nửa ngày = 0,5; ngày làm bù = 1) */
  standardDaysInMonth(ym: string): number;
  /** giờ `hhmm` của ngày `day`, kéo vào giờ làm việc: ngày nghỉ → ngày làm việc kế; sau cuối ca → cuối ca; trước đầu ca → đầu ca */
  atOnWorkDay(day: string, hhmm: string): string;
  /** lùi `t` về mốc làm việc gần nhất trước đó (chính `t` nếu đang trong ca; CN 10:00 → T7 cuối ca) */
  clampBack(t: string | Date): string;
  /** lùi `hours` giờ làm việc (= addHoursIso(t, -hours)) */
  subHours(t: string | Date, hours: number): string;
  /** đang trong giờ làm việc */
  isWorkingAt(t: string | Date): boolean;
  /** thời điểm làm việc gần nhất từ `t` (chính `t` nếu đang trong ca) */
  nextWorkStart(t: string | Date): string;
  /** cộng `hours` giờ làm việc (âm = lùi); kết quả ISO giờ VN. Làm tròn tới phút (mất giây). `hours = 0`: giữ nguyên mốc nếu đang trong ca, không thì đẩy tới đầu ca kế */
  addHoursIso(t: string | Date, hours: number): string;
  /** số giờ làm việc giữa hai thời điểm (âm nếu b trước a) */
  hoursBetween(a: string | Date, b: string | Date): number;
  /** ngày làm việc thứ `n` sau (n > 0) / trước (n < 0) ngày `day`; n = 0 trả về chính ngày nếu là ngày làm việc, không thì ngày làm việc kế */
  addWorkDays(day: string, n: number): string;
  /** số ngày làm việc trong (from, to] — giống đếm "còn N ngày" */
  workDaysBetween(from: string, to: string): number;
}

export function createWorkCalendar(config: WorkConfig): WorkCalendar {
  const hol = (key: string) => config.holidays.find((h) => key >= h.from && key <= h.to);
  const isWorkDayNo = (dayNo: number) => {
    const key = dayKey(dayNo);
    if (config.overrides.has(key)) return true;
    if (hol(key)) return false;
    return config.weekdays.has(weekdayOf(dayNo));
  };
  const shiftsNo = (dayNo: number): Array<readonly [Minute, Minute]> => {
    if (!isWorkDayNo(dayNo)) return [];
    const key = dayKey(dayNo);
    return rawShifts(dayNo, key).filter(([s, e]) => e > s);
  };
  const rawShifts = (dayNo: number, key: string): Array<readonly [Minute, Minute]> => {
    // thứ Bảy (không phải ngày làm bù): một ca từ đầu ca sáng tới WorkSatEnd;
    // nếu WorkSatEnd muộn hơn giờ vào ca chiều thì tách 2 ca (ca sáng + chiều tới WorkSatEnd), trừ nghỉ trưa
    if (weekdayOf(dayNo) === 6 && config.satEnd !== null && !config.overrides.has(key)) {
      if (config.satEnd > config.afternoon[0]) return [config.morning, [config.afternoon[0], config.satEnd] as const];
      return [[config.morning[0], config.satEnd] as const];
    }
    return [config.morning, config.afternoon];
  };
  const dayHoursNo = (dayNo: number) => shiftsNo(dayNo).reduce((a, [s, e]) => a + e - s, 0) / 60;
  const dayHours = (day: string) => dayHoursNo(dayNoOf(day));
  const fullDay = (config.morning[1] - config.morning[0] + config.afternoon[1] - config.afternoon[0]) / 60;
  const stepWorkDay = (day: string, step: 1 | -1) => {
    let d = dayNoOf(day);
    for (let i = 0; i < LIMIT && (i === 0 || !isWorkDayNo(d)); i++) d += step;
    return dayKey(d);
  };
  const keyOf = (day: string | Date) => (typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : dayKey(Math.floor(toLocal(day) / DAY_MIN)));

  /** đoạn làm việc [start, end) tuyệt đối của ngày */
  const spans = (dayNo: number) => shiftsNo(dayNo).map(([s, e]) => [dayNo * DAY_MIN + s, dayNo * DAY_MIN + e] as const);

  // giới hạn quét: 3 năm, tránh lặp vô hạn khi cấu hình không có ngày làm việc nào
  const LIMIT = 366 * 3;

  const forward = (from: Local, minutes: number): Local => {
    let left = minutes;
    let day = Math.floor(from / DAY_MIN);
    for (let i = 0; i < LIMIT; i++, day++) {
      for (const [s, e] of spans(day)) {
        const a = Math.max(s, from);
        if (a >= e) continue;
        if (a + left <= e) return a + left;
        left -= e - a;
      }
    }
    throw new Error('Không tìm thấy giờ làm việc trong 3 năm tới — kiểm tblClientInfo');
  };
  const backward = (from: Local, minutes: number): Local => {
    let left = minutes;
    let day = Math.floor(from / DAY_MIN);
    for (let i = 0; i < LIMIT; i++, day--) {
      for (const [s, e] of [...spans(day)].reverse()) {
        const b = Math.min(e, from);
        if (b <= s) continue;
        if (b - left >= s) return b - left;
        left -= b - s;
      }
    }
    throw new Error('Không tìm thấy giờ làm việc trong 3 năm trước — kiểm tblClientInfo');
  };
  const worked = (a: Local, b: Local): number => {
    let sum = 0;
    for (let day = Math.floor(a / DAY_MIN); day <= Math.floor(b / DAY_MIN); day++) {
      for (const [s, e] of spans(day)) sum += Math.max(0, Math.min(e, b) - Math.max(s, a));
    }
    return sum;
  };

  return {
    config,
    isWorkDay: (day) => isWorkDayNo(dayNoOf(keyOf(day))),
    holidayOf: (day) => hol(day),
    shiftsOf: (day) => shiftsNo(dayNoOf(day)),
    dayHours,
    presenceHours: ((a: string | Date, b?: string | Date) => {
      if (b === undefined) return dayHours(a as string);
      const x = toLocal(a);
      const y = toLocal(b);
      if (y <= x) return 0;
      let gap = 0;
      for (let day = Math.floor(x / DAY_MIN); day <= Math.floor(y / DAY_MIN); day++) {
        const g0 = day * DAY_MIN + config.morning[1];
        const g1 = day * DAY_MIN + config.afternoon[0];
        gap += Math.max(0, Math.min(g1, y) - Math.max(g0, x));
      }
      return (y - x - gap) / 60;
    }) as WorkCalendar['presenceHours'],
    isHoliday: (day) => hol(day) !== undefined,
    dayKind: (day) => {
      const no = dayNoOf(day);
      if (config.overrides.has(day)) return 'makeup';
      const h = hol(day);
      if (h) return h.company ? 'companyHoliday' : 'holiday';
      if (!isWorkDayNo(no)) return 'off';
      return dayHours(day) < fullDay ? 'halfDay' : 'work';
    },
    holidaysInRange: (from, to) => config.holidays.filter((h) => h.from <= to && h.to >= from),
    prevWorkDay: (day) => stepWorkDay(day, -1),
    nextWorkDay: (day) => stepWorkDay(day, 1),
    standardDaysInMonth: (ym) => {
      const m = /^(\d{4})-(\d{2})$/.exec(ym);
      if (!m) throw new Error(`Tháng không hợp lệ: ${ym}`);
      const first = dayNoOf(`${ym}-01`);
      const next = dayNoOf(Number(m[2]) === 12 ? `${Number(m[1]) + 1}-01-01` : `${m[1]}-${pad(Number(m[2]) + 1)}-01`);
      let sum = 0;
      for (let d = first; d < next; d++) sum += dayHoursNo(d) / fullDay;
      return sum;
    },
    atOnWorkDay: (day, hhmm) => {
      let no = dayNoOf(day);
      for (let i = 0; i < LIMIT && !isWorkDayNo(no); i++) no++;
      const sh = shiftsNo(no);
      const t = parseHHMM(hhmm);
      const m = sh.length ? Math.min(Math.max(t, sh[0]![0]), sh[sh.length - 1]![1]) : t;
      return toIso(no * DAY_MIN + m);
    },
    clampBack: (t) => toIso(backward(toLocal(t), 0)),
    subHours: (t, hours) => toIso(backward(toLocal(t), Math.round(hours * 60))),
    isWorkingAt: (t) => {
      const l = toLocal(t);
      return spans(Math.floor(l / DAY_MIN)).some(([s, e]) => l >= s && l < e);
    },
    nextWorkStart: (t) => toIso(forward(toLocal(t), 0)),
    addHoursIso: (t, hours) => {
      const m = Math.round(hours * 60);
      const l = toLocal(t);
      return toIso(m >= 0 ? forward(l, m) : backward(l, -m));
    },
    hoursBetween: (a, b) => {
      const x = toLocal(a);
      const y = toLocal(b);
      return (x <= y ? worked(x, y) : -worked(y, x)) / 60;
    },
    addWorkDays: (day, n) => {
      let d = dayNoOf(day);
      if (n === 0) {
        for (let i = 0; i < LIMIT && !isWorkDayNo(d); i++) d++;
        return dayKey(d);
      }
      n = Math.trunc(n);
      const step = n > 0 ? 1 : -1;
      let k = Math.abs(n);
      for (let i = 0; i < LIMIT * 2 && k > 0; i++) {
        d += step;
        if (isWorkDayNo(d)) k--;
      }
      if (k > 0) throw new Error('Không đủ ngày làm việc — kiểm tblClientInfo');
      return dayKey(d);
    },
    workDaysBetween: (from, to) => {
      const a = dayNoOf(from);
      const b = dayNoOf(to);
      const [lo, hi, sign] = a <= b ? [a, b, 1] : [b, a, -1];
      let c = 0;
      for (let d = lo + 1; d <= hi; d++) if (isWorkDayNo(d)) c++;
      return c * sign;
    },
  };
}
